// Smart Sash Admin Panel - Device Service
// CRUD operations for device management (v2 API)

import { axiosClient } from '../http/axiosClient';
import {
  DeviceDTO,
  CreateDeviceRequest,
  UpdateDeviceRequest,
  PaginatedResponse,
  PaginationParams,
  ApiPaginatedData,
  toPaginatedResponse,
  EmergencyContactDTO,
} from '@/models';

const BASE_PATH = '/Device';

/**
 * Device Service (v2)
 */
export const deviceService = {
  /**
   * Get all devices with pagination and optional filters
   */
  getAll: async (
    params?: PaginationParams & { institutionId?: string; labId?: string }
  ): Promise<PaginatedResponse<DeviceDTO>> => {
    const response = await axiosClient.get<ApiPaginatedData<DeviceDTO>>(BASE_PATH, {
      params,
    });
    return toPaginatedResponse(response.data);
  },

  /**
   * Get device by ID
   */
  getById: async (id: string): Promise<DeviceDTO> => {
    const response = await axiosClient.get<DeviceDTO>(`${BASE_PATH}/${id}`);
    return response.data;
  },

  /**
   * Get device by serial number
   */
  getBySerialNumber: async (serialNumber: string): Promise<DeviceDTO> => {
    const response = await axiosClient.get<DeviceDTO>(`${BASE_PATH}/serial/${serialNumber}`);
    return response.data;
  },

  /**
   * Get device by MAC address
   */
  getByMacAddress: async (macAddress: string): Promise<DeviceDTO> => {
    const response = await axiosClient.get<DeviceDTO>(`${BASE_PATH}/mac/${macAddress}`);
    return response.data;
  },

  /**
   * Create new device
   */
  create: async (data: CreateDeviceRequest): Promise<DeviceDTO> => {
    const response = await axiosClient.post<DeviceDTO>(BASE_PATH, data);
    return response.data;
  },

  /**
   * Update existing device
   */
  update: async (id: string, data: UpdateDeviceRequest): Promise<DeviceDTO> => {
    const response = await axiosClient.put<DeviceDTO>(`${BASE_PATH}/${id}`, data);
    return response.data;
  },

  /**
   * Delete device
   */
  delete: async (id: string): Promise<void> => {
    await axiosClient.delete(`${BASE_PATH}/${id}`);
  },

  /**
   * Get device emergency contact
   */
  getEmergencyContact: async (id: string): Promise<EmergencyContactDTO> => {
    const response = await axiosClient.get<EmergencyContactDTO>(
      `${BASE_PATH}/${id}/emergency-contact`
    );
    return response.data;
  },
};

export default deviceService;
